import { useMutation, useQuery } from '@apollo/client/react';
import { GET_BASIC_CUSTOMER_DATA_QUERY } from '../../integrations/allkort/graphql/gql/CustomerQueries';
import { ADD_NEW_CARD_CUSTOMER_TASK_MUTATION } from '../../integrations/allkort/graphql/gql/CustomerTasksMutations';
import { BasicCustomerDataResponse } from '../../integrations/allkort/graphql/interface/Customer';
import {
	AddNewCardCustomerTaskInput,
	AddNewCardCustomerTaskResponse,
} from '../../integrations/allkort/graphql/interface/CustomerTaskMutation';

/**
 * useAddNewCardTask
 *
 * Custom hook for submitting an "order new card" customer task.
 * Fetches the current customer's ID from cache and exposes the mutation.
 *
 * @example
 * ```tsx
 * const { addNewCardTask, loading, error } = useAddNewCardTask();
 *
 * await addNewCardTask({
 *   cardTypeId: 2,     // 1 = Tankkort/Företagskort, 2 = Smartkort
 *   pinCode: 1414,
 *   reference2: 'My Card',
 * });
 * ```
 */
export const useAddNewCardTask = () => {
	const { data: customerData } = useQuery<BasicCustomerDataResponse>(
		GET_BASIC_CUSTOMER_DATA_QUERY,
		{ fetchPolicy: 'network-only' }
	);

	const [addNewCardCustomerTaskMutation, { loading, error, data }] = useMutation<
		AddNewCardCustomerTaskResponse,
		AddNewCardCustomerTaskInput
	>(ADD_NEW_CARD_CUSTOMER_TASK_MUTATION);

	const addNewCardTask = async (input: Pick<AddNewCardCustomerTaskInput, 'cardTypeId' | 'pinCode' | 'reference1' | 'reference2'>) => {
		const customerId = customerData?.currentCustomer?.id;
		console.log('[useAddNewCardTask] customerId:', customerId);
		if (!customerId) {
			throw new Error('Customer ID not available');
		}
		const variables = {
			cardTypeId: input.cardTypeId,
			customerId,
			pinCode: input.pinCode,
			reference1: input.reference1 ?? '',
			reference2: input.reference2,
		};
		console.log('[useAddNewCardTask] Calling mutation with variables:', variables);
		const result = await addNewCardCustomerTaskMutation({ variables });
		console.log('[useAddNewCardTask] Raw mutation result:', result);
		if (result.error) {
			console.error('[useAddNewCardTask] GraphQL errors:', result.error);
		}
		return result;
	};

	return { addNewCardTask, loading, error, data };
};
